'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState } from 'react';
import { useAlbumFlightStore } from './albumFlight';
import { DailyCardWidget } from './DailyCardWidget';
import { useDailyCardStore } from './dailyCardStore';
import { loadDailyPickDialog, loadHubFlightLayer, loadJokerPickDialog } from './dailyPickLoader';

const DailyPickDialog = dynamic(loadDailyPickDialog, { ssr: false });
const JokerPickDialog = dynamic(loadJokerPickDialog, { ssr: false });
const RewardsHubFlightLayer = dynamic(loadHubFlightLayer, { ssr: false });

interface GlobalDailyCardProps {
  /** The floating desktop card; off where the corner is taken (the trade terminal). */
  floating?: boolean;
}

/**
 * Mounted once per zone layout: the floating widget, the pick dialog both it
 * and the header icon open, and the layer the card flies on to the hub in.
 */
export function GlobalDailyCard({ floating = true }: GlobalDailyCardProps) {
  // The dialog mounts on the first open and stays, so its close can animate.
  const [engaged, setEngaged] = useState(false);
  const [jokerOpen, setJokerOpen] = useState(false);
  const open = useDailyCardStore((s) => s.open);
  const setOpen = useDailyCardStore((s) => s.setOpen);
  const setHosted = useDailyCardStore((s) => s.setHosted);

  useEffect(() => {
    setHosted(true);
    return () => setHosted(false);
  }, [setHosted]);

  // Mounted in every zone layout that can pick a card, so "New" comes back
  // after hydration and before any pick could write over the stored list.
  useEffect(() => {
    void useAlbumFlightStore.persist.rehydrate();
  }, []);

  useEffect(() => {
    if (open) setEngaged(true);
  }, [open]);

  // A joker is chosen right here, as on the hub's day tile — no trip to the hub.
  const handleOpenJoker = useCallback(() => {
    setOpen(false);
    setJokerOpen(true);
  }, [setOpen]);

  return (
    <>
      {floating ? <DailyCardWidget /> : null}
      {engaged ? (
        <>
          <DailyPickDialog
            open={open}
            onOpenChange={setOpen}
            onViewAlbum={handleOpenJoker}
            showHubActions
          />
          <JokerPickDialog open={jokerOpen} onOpenChange={setJokerOpen} />
          <RewardsHubFlightLayer />
        </>
      ) : null}
    </>
  );
}
